import React, { useState, useEffect, useCallback, useRef } from 'react';
import MPDashboardLayout from '../../components/mp/MPDashboardLayout';
import { postsApi, exercisesApi, platformsApi, mediaApi } from '../../lib/mpApi';
import { NewsTemplate, isNewsPlatform } from '../../components/mp/SocialPostTemplates';
import NewsArticleDetailDialog from '../../components/mp/NewsArticleDetailDialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { TIMEZONES } from '../../lib/timezone';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import {
  Plus, Search, Edit, Trash2, Send, Loader2, Newspaper, FileUp, X, Film, CalendarClock,
  ImagePlus, GripVertical, Trash, Type,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const resolveUrl = (url) => url?.startsWith('/') ? `${BACKEND_URL}${url}` : url;

// Rich content block editor - each block is either text or image
const ContentBlockEditor = ({ blocks, onChange, onImageUpload }) => {
  const addTextBlock = (afterIndex) => {
    const newBlocks = [...blocks];
    newBlocks.splice(afterIndex + 1, 0, { type: 'text', content: '' });
    onChange(newBlocks);
  };

  const addImageBlock = async (afterIndex) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      try {
        const result = await onImageUpload(file);
        const newBlocks = [...blocks];
        newBlocks.splice(afterIndex + 1, 0, { type: 'image', url: result, caption: '' });
        onChange(newBlocks);
      } catch { toast.error('Image upload failed'); }
    };
    input.click();
  };

  const updateBlock = (index, data) => {
    const newBlocks = [...blocks];
    newBlocks[index] = { ...newBlocks[index], ...data };
    onChange(newBlocks);
  };

  const removeBlock = (index) => {
    if (blocks.length <= 1) return;
    onChange(blocks.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-2">
      {blocks.map((block, i) => (
        <div key={i} className="group relative">
          {block.type === 'text' ? (
            <div className="relative">
              <Textarea
                value={block.content}
                onChange={e => updateBlock(i, { content: e.target.value })}
                placeholder={i === 0 ? 'Lead paragraph...' : 'Continue article...'}
                rows={3}
                className="bg-muted border-input placeholder:text-muted-foreground pr-10"
              />
              {blocks.length > 1 && (
                <button onClick={() => removeBlock(i)}
                  className="absolute top-2 right-2 text-muted-foreground hover:text-red-400 transition opacity-0 group-hover:opacity-100">
                  <Trash className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          ) : (
            <div className="relative border border-input rounded-lg overflow-hidden bg-muted/50">
              <img src={resolveUrl(block.url)} alt="" className="w-full max-h-64 object-cover" />
              <div className="p-2">
                <Input
                  value={block.caption || ''}
                  onChange={e => updateBlock(i, { caption: e.target.value })}
                  placeholder="Image caption (optional)"
                  className="bg-transparent border-0 text-xs text-muted-foreground placeholder:text-muted-foreground h-7 p-0 focus-visible:ring-0"
                />
              </div>
              <button onClick={() => removeBlock(i)}
                className="absolute top-2 right-2 w-6 h-6 rounded-full bg-black/60 text-foreground flex items-center justify-center hover:bg-red-600 transition opacity-0 group-hover:opacity-100">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
          {/* Insert controls between blocks */}
          <div className="flex items-center justify-center gap-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <button onClick={() => addTextBlock(i)}
              className="flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground bg-muted px-2 py-1 rounded border border-input">
              <Type className="w-3 h-3" /> Text
            </button>
            <button onClick={() => addImageBlock(i)}
              className="flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground bg-muted px-2 py-1 rounded border border-input">
              <ImagePlus className="w-3 h-3" /> Image
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

// Convert blocks to flat content and media_urls for storage
const blocksToPayload = (blocks) => {
  const contentParts = [];
  const mediaUrls = [];
  blocks.forEach(block => {
    if (block.type === 'text' && block.content) {
      contentParts.push(block.content);
    } else if (block.type === 'image' && block.url) {
      mediaUrls.push(block.url);
      contentParts.push(`[IMAGE: ${block.caption || 'Photo'}]`);
    }
  });
  return { content: contentParts.join('\n\n'), media_urls: mediaUrls };
};

// Convert flat content + media back to blocks
const payloadToBlocks = (content, mediaUrls) => {
  if (!content) return [{ type: 'text', content: '' }];
  const parts = content.split('\n\n');
  const blocks = [];
  let mediaIdx = 0;
  parts.forEach(part => {
    const imgMatch = part.match(/^\[IMAGE: (.+)\]$/);
    if (imgMatch && mediaIdx < (mediaUrls || []).length) {
      blocks.push({ type: 'image', url: mediaUrls[mediaIdx], caption: imgMatch[1] === 'Photo' ? '' : imgMatch[1] });
      mediaIdx++;
    } else {
      blocks.push({ type: 'text', content: part });
    }
  });
  // Add remaining media as image blocks
  while (mediaIdx < (mediaUrls || []).length) {
    blocks.push({ type: 'image', url: mediaUrls[mediaIdx], caption: '' });
    mediaIdx++;
  }
  return blocks.length > 0 ? blocks : [{ type: 'text', content: '' }];
};

const MPNewsPostsPage = () => {
  const [posts, setPosts] = useState([]);
  const [exercises, setExercises] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterExercise, setFilterExercise] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showDialog, setShowDialog] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [saving, setSaving] = useState(false);
  const [contentBlocks, setContentBlocks] = useState([{ type: 'text', content: '' }]);
  const [detailPost, setDetailPost] = useState(null);
  const [formData, setFormData] = useState({
    exercise_id: '', platform: '', title: '', status: 'draft',
    author: { name: '', handle: '', bio: '', followers: 0 },
    engagement: { likes: 0, comments: 0, shares: 0, views: 0 },
    category: '', location: '', media_urls: [],
  });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [postsData, exData, platData] = await Promise.all([
        postsApi.getAll({ post_type: 'news' }),
        exercisesApi.getAll().catch(() => []),
        platformsApi.getAll().catch(() => []),
      ]);
      setPosts(Array.isArray(postsData) ? postsData.filter(p => p.post_type === 'news') : []);
      setExercises(Array.isArray(exData) ? exData : []);
      setPlatforms(Array.isArray(platData) ? platData.filter(p => isNewsPlatform(p.name) || ['news', 'custom', 'regional'].includes(p.category)) : []);
    } catch { toast.error('Failed to load posts'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const filtered = posts.filter(p => {
    if (search && !p.content?.toLowerCase().includes(search.toLowerCase()) && !p.title?.toLowerCase().includes(search.toLowerCase())) return false;
    if (filterExercise !== 'all' && p.exercise_id !== filterExercise) return false;
    if (filterStatus !== 'all' && p.status !== filterStatus) return false;
    return true;
  });

  // Filter platforms by selected exercise's selected_platforms
  const selectedExObj = exercises.find(e => e.exercise_id === formData.exercise_id);
  const formPlatforms = selectedExObj?.selected_platforms?.length > 0
    ? platforms.filter(p => selectedExObj.selected_platforms.includes(p.platform_id) || selectedExObj.selected_platforms.includes(p.name))
    : platforms;

  const handleImageUpload = async (file) => {
    const result = await mediaApi.upload(file);
    return result.file_url;
  };

  const openCreate = () => {
    setEditingPost(null);
    setContentBlocks([{ type: 'text', content: '' }]);
    setFormData({
      exercise_id: exercises[0]?.exercise_id || '', platform: '', title: '', status: 'draft',
      author: { name: '', handle: '', bio: '', followers: 0 },
      engagement: { likes: 0, comments: 0, shares: 0, views: 0 },
      category: '', location: '', media_urls: [],
      schedule: null,
    });
    setShowDialog(true);
  };

  const openEdit = (post) => {
    setEditingPost(post);
    setContentBlocks(payloadToBlocks(post.content, post.media_urls));
    setFormData({
      exercise_id: post.exercise_id, platform: post.platform,
      title: post.title || '', status: post.status,
      author: post.author || { name: '', handle: '', bio: '', followers: 0 },
      engagement: post.engagement || { likes: 0, comments: 0, shares: 0, views: 0 },
      category: post.category || '', location: post.location || '', media_urls: post.media_urls || [],
      schedule: post.schedule || null,
    });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!formData.exercise_id || !formData.platform) {
      toast.error('Exercise and platform are required'); return;
    }
    const { content, media_urls } = blocksToPayload(contentBlocks);
    if (!content.trim() && !formData.title) {
      toast.error('Headline or content is required'); return;
    }
    setSaving(true);
    try {
      const payload = { ...formData, content, media_urls, post_type: 'news' };
      if (editingPost) {
        await postsApi.update(editingPost.post_id, payload);
        toast.success('Article updated');
      } else {
        await postsApi.create(payload);
        toast.success('Article created');
      }
      setShowDialog(false);
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (post) => {
    if (!window.confirm('Delete this article?')) return;
    try { await postsApi.delete(post.post_id); toast.success('Deleted'); loadData(); }
    catch { toast.error('Delete failed'); }
  };

  const handlePublish = async (post) => {
    try { await postsApi.publish(post.post_id); toast.success('Published'); loadData(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Publish failed'); }
  };

  const handleUnpublish = async (post) => {
    if (!window.confirm(`Unpublish this article? It will return to draft and disappear from Live Display.`)) return;
    try { await postsApi.unpublish(post.post_id); toast.success('Unpublished'); loadData(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Unpublish failed'); }
  };

  const newsPlatformColors = { bbc: '#BB1919', cnn: '#CC0000', reuters: '#FF8000', aljazeera: '#D4AA00', nytimes: '#000', guardian: '#052962', foxnews: '#003366', rt: '#5EAA22', xinhua: '#D52B1E' };

  return (
    <MPDashboardLayout title="News Articles">
      <div className="space-y-6" data-testid="mp-news-posts-page">
        {/* Toolbar */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input placeholder="Search articles..." value={search} onChange={e => setSearch(e.target.value)}
              className="pl-9 bg-muted/50 border-input placeholder:text-muted-foreground" data-testid="mp-news-search" />
          </div>
          <Select value={filterExercise} onValueChange={setFilterExercise}>
            <SelectTrigger className="w-40 bg-muted/50 border-input"><SelectValue placeholder="Exercise" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Exercises</SelectItem>{exercises.map(e => <SelectItem key={e.exercise_id} value={e.exercise_id}>{e.name}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-32 bg-muted/50 border-input"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All</SelectItem><SelectItem value="draft">Draft</SelectItem><SelectItem value="published">Published</SelectItem><SelectItem value="scheduled">Scheduled</SelectItem></SelectContent>
          </Select>
          <Button onClick={openCreate} className="bg-red-600 hover:bg-red-700 text-foreground" data-testid="mp-create-news-btn">
            <Plus className="w-4 h-4 mr-2" /> New Article
          </Button>
        </div>

        {/* News Grid */}
        {loading ? (
          <div className="flex items-center justify-center h-40"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground"><Newspaper className="w-12 h-12 mx-auto mb-3 opacity-30" /><p className="text-sm">No news articles found</p></div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {filtered.map(post => (
              <div key={post.post_id} className="relative group cursor-pointer" data-testid={`mp-news-post-${post.post_id}`}
                onClick={() => setDetailPost(post)}>
                <NewsTemplate post={post} platformColor={newsPlatformColors[post.platform?.toLowerCase()] || '#333'} />
                <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Badge className={`text-[10px] border ${
                    post.status === 'published' ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' : 'bg-muted0/20 text-muted-foreground border-border/30'
                  }`}>{post.status}</Badge>
                </div>
                <div className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1"
                  onClick={e => e.stopPropagation()}>
                  {post.status === 'draft' && (
                    <Button size="sm" onClick={() => handlePublish(post)} className="bg-emerald-600 hover:bg-emerald-700 text-foreground h-7 text-xs">
                      <Send className="w-3 h-3 mr-1" />Publish
                    </Button>
                  )}
                  {post.status === 'published' && (
                    <Button size="sm" onClick={() => handleUnpublish(post)} variant="outline" className="h-7 text-xs" data-testid={`unpublish-${post.post_id}`}>
                      Unpublish
                    </Button>
                  )}
                  <Button size="sm" variant="secondary" onClick={() => openEdit(post)} className="h-7 bg-muted hover:bg-muted/80"><Edit className="w-3 h-3" /></Button>
                  <Button size="sm" variant="destructive" onClick={() => handleDelete(post)} className="h-7"><Trash2 className="w-3 h-3" /></Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create/Edit Dialog — Block Editor */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto ">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase text-foreground">{editingPost ? 'Edit Article' : 'Create Article'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Exercise</Label>
                <Select value={formData.exercise_id} onValueChange={v => setFormData({...formData, exercise_id: v})}>
                  <SelectTrigger className="bg-muted border-input"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{exercises.map(e => <SelectItem key={e.exercise_id} value={e.exercise_id}>{e.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Platform</Label>
                <Select value={formData.platform} onValueChange={v => setFormData({...formData, platform: v})}>
                  <SelectTrigger className="bg-muted border-input"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{formPlatforms.map(p => <SelectItem key={p.platform_id} value={p.name}>{p.display_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Headline</Label>
              <Input value={formData.title} onChange={e => setFormData({...formData, title: e.target.value})}
                placeholder="Breaking: Major headline here..."
                className="bg-muted border-input text-lg font-semibold placeholder:text-muted-foreground"
                data-testid="mp-news-title-input" />
            </div>

            {/* Block Editor */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Article Body</Label>
                <div className="flex gap-1">
                  <button onClick={() => setContentBlocks([...contentBlocks, { type: 'text', content: '' }])}
                    className="flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground bg-muted px-2 py-1 rounded border border-input">
                    <Type className="w-3 h-3" /> Add Text
                  </button>
                  <button onClick={async () => {
                    const input = document.createElement('input');
                    input.type = 'file'; input.accept = 'image/*';
                    input.onchange = async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      try {
                        const result = await mediaApi.upload(file);
                        setContentBlocks([...contentBlocks, { type: 'image', url: result.file_url, caption: '' }]);
                      } catch { toast.error('Upload failed'); }
                    };
                    input.click();
                  }}
                    className="flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground bg-muted px-2 py-1 rounded border border-input">
                    <ImagePlus className="w-3 h-3" /> Add Image
                  </button>
                </div>
              </div>
              <ContentBlockEditor blocks={contentBlocks} onChange={setContentBlocks} onImageUpload={handleImageUpload} />
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Author</Label>
                <Input value={formData.author.name} onChange={e => setFormData({...formData, author: {...formData.author, name: e.target.value}})}
                  placeholder="Author name" className="bg-muted border-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Category</Label>
                <Input value={formData.category} onChange={e => setFormData({...formData, category: e.target.value})}
                  placeholder="e.g. Politics, World" className="bg-muted border-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Location</Label>
                <Input value={formData.location} onChange={e => setFormData({...formData, location: e.target.value})}
                  placeholder="e.g. London, UK" className="bg-muted border-input" />
              </div>
            </div>

            {/* Schedule */}
            <div className="bg-muted/50 rounded-lg p-3 border border-input space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                  <CalendarClock className="w-3.5 h-3.5" /> Schedule
                </Label>
                <Switch checked={formData.status === 'scheduled'}
                  onCheckedChange={v => setFormData({
                    ...formData, status: v ? 'scheduled' : 'draft',
                    schedule: v ? { scheduled: true, publish_datetime: '', timezone: 'UTC' } : null,
                  })} data-testid="mp-news-schedule-toggle" />
              </div>
              {formData.status === 'scheduled' && (
                <div className="grid grid-cols-2 gap-3">
                  <Input type="datetime-local" value={formData.schedule?.publish_datetime || ''}
                    onChange={e => setFormData({...formData, schedule: { ...formData.schedule, scheduled: true, publish_datetime: e.target.value }})}
                    className="bg-muted border-input h-8 text-sm" data-testid="mp-news-schedule-datetime" />
                  <Select value={formData.schedule?.timezone || 'UTC'}
                    onValueChange={v => setFormData({...formData, schedule: { ...formData.schedule, timezone: v }})}>
                    <SelectTrigger className="bg-muted border-input h-8 text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {TIMEZONES.map(tz => (
                        <SelectItem key={tz.value} value={tz.value}>{tz.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </div>
          <DialogFooter className="border-t border-border pt-4">
            <Button variant="outline" onClick={() => setShowDialog(false)} className="border-input text-muted-foreground hover:bg-muted">Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-red-600 hover:bg-red-700 text-foreground" data-testid="mp-news-save-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <NewsArticleDetailDialog open={!!detailPost} onOpenChange={(o) => !o && setDetailPost(null)} post={detailPost} />
    </MPDashboardLayout>
  );
};

export default MPNewsPostsPage;
